/**
 * Une période de consultation : les présélections, et les dates qu'elles couvrent.
 *
 * Dans `ui/` et non dans `parent/` depuis le 6 septembre 2026, quand « Ma carte »
 * — un écran **enfant** — a eu besoin du même choix. La coquille enfant
 * n'importe pas `parent/` (voir le pied de `parent/effort`), et récrire les
 * bornes de son côté aurait donné deux « depuis la rentrée » qui divergent le
 * jour où l'un des deux change. Même précédent que `enClair`.
 *
 * `parent/effort` les réexporte pour ses appelants existants.
 */

/** Les présélections offertes au parent, dans l'ordre d'affichage. */
export type Preselection = "7j" | "30j" | "rentree" | "libre";

export type Periode = { since: string; until: string };

/** Une date en `YYYY-MM-DD`, dans le calendrier local — jamais en UTC.
 *
 * `toISOString()` convertit en UTC avant de découper, si bien qu'un soir d'été
 * à Paris rend la veille. Les bornes que le parent choisit sont des dates de
 * calendrier ; les décaler d'un jour retirerait de l'écran le travail de la
 * soirée, c'est-à-dire tout le travail. */
export function jour(date: Date): string {
  const mois = String(date.getMonth() + 1).padStart(2, "0");
  const quantieme = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${mois}-${quantieme}`;
}

function decale(depuis: Date, jours: number): Date {
  const copie = new Date(depuis);
  copie.setDate(copie.getDate() + jours);
  return copie;
}

/**
 * Le 1ᵉʳ septembre de l'année scolaire en cours.
 *
 * En juillet et en août, c'est encore celui de l'année passée : l'année
 * scolaire de l'élève, et non le calendrier — la même règle que
 * `curriculum.in_school_year` côté serveur. Sans elle, ouvrir l'écran le
 * 15 août montrerait une période vide et laisserait croire qu'il n'a rien
 * travaillé de l'année.
 */
export function rentree(aujourdhui: Date): Date {
  const annee = aujourdhui.getMonth() >= 8 ? aujourdhui.getFullYear() : aujourdhui.getFullYear() - 1;
  return new Date(annee, 8, 1);
}

/**
 * Les deux bornes d'une présélection, incluses.
 *
 * **Il n'y a pas de « trimestre ».** Un trimestre scolaire n'a pas de dates
 * connues d'ici : elles dépendent de l'établissement, et le conseil de classe
 * de Baya ne tombe pas le même jour que celui de Mehdi. Les inventer donnerait
 * un intitulé faux sur des chiffres justes, ce qui est pire qu'une période
 * saisie à la main — et la saisie à la main existe juste à côté.
 */
export function bornes(choix: Preselection, aujourdhui: Date): Periode | null {
  const fin = jour(aujourdhui);
  if (choix === "7j") return { since: jour(decale(aujourdhui, -6)), until: fin };
  if (choix === "30j") return { since: jour(decale(aujourdhui, -29)), until: fin };
  if (choix === "rentree") return { since: jour(rentree(aujourdhui)), until: fin };
  return null;
}

export const PRESELECTIONS: { valeur: Preselection; libelle: string }[] = [
  { valeur: "7j", libelle: "7 derniers jours" },
  { valeur: "30j", libelle: "30 derniers jours" },
  { valeur: "rentree", libelle: "Depuis la rentrée" },
  { valeur: "libre", libelle: "Autres dates" },
];
